/**
 * Helpers for the annual-report P&L comparison (GET annual-report/{caseId}/{year}/profit-and-loss/comparison).
 *
 * The annual report keeps amounts in report direction (income and expenses both positive); the P&L reports
 * and Form 6111 use the source sign (income positive, expenses negative).
 */

const num = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

export const toSourceSign = (amount, isIncome) => (isIncome ? num(amount) : -num(amount));

/** Saved minus live for one line; null when the live source was not read. */
export const lineDifference = (line, liveIncluded) => {
    if (!liveIncluded) return null;
    return num(line.amount) - num(line.liveAmount);
};

export const netProfitDifference = (comparison) => {
    if (!comparison?.liveIncluded || comparison.liveNetProfit == null) return null;
    return num(comparison.netProfit) - num(comparison.liveNetProfit);
};

const isSaved = (line) => !line.isNewInSource;

/**
 * The annual P&L as Form 6111 detail groups (the sort-code report shape CodesReport6111 aggregates):
 * one row per business line, both years, signed like the source. Annex B lines are left out, as in the net profit.
 */
export const buildAnnual6111Details = (comparison, year) => {
    if (!comparison?.lines?.length || !year) return [];
    const groups = new Map();
    comparison.lines
        .filter((line) => isSaved(line) && !line.isAnnexB)
        .forEach((line) => {
            const groupName = (line.groupName || '').trim();
            if (!groups.has(groupName)) groups.set(groupName, { groupName, rows: [] });
            const years = [{ year, amount: toSourceSign(line.amount, line.isIncome) }];
            if (line.priorYearAmount != null) {
                years.push({ year: year - 1, amount: toSourceSign(line.priorYearAmount, line.isIncome) });
            }
            groups.get(groupName).rows.push({
                sortCodeId: line.lineKey,
                sortCodeName: line.description,
                code6111Id: line.code6111Id ?? null,
                code6111Number: line.code6111Number ?? '',
                years,
            });
        });
    return [...groups.values()];
};

/** Rows for a comparison table: saved lines and lines the source added since the fetch, with differences. */
export const buildComparisonRows = (comparison) => {
    if (!comparison?.lines) return [];
    const liveIncluded = Boolean(comparison.liveIncluded);
    return comparison.lines.map((line, index) => ({
        ...line,
        id: `${line.lineKeyType}:${line.lineKey}:${index}`,
        difference: lineDifference(line, liveIncluded),
    }));
};

export const formatSignedAmount = (value) => {
    if (value == null || !Number.isFinite(Number(value))) return '—';
    const amount = Number(value);
    const formatted = Math.abs(amount).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return amount < 0 ? `(${formatted})` : formatted;
};

/** True when a difference is worth showing (ignores rounding). */
export const isMeaningfulDifference = (value) => value != null && Math.abs(Number(value)) >= 0.005;
